import type { Scene } from '../scene/Scene';
import type { CompositeInstance } from './CompositeInstance';
import { requireTemplate, resolveState } from './Template';
import type { CompositeDef, SceneCompositeInstance } from './types';

/**
 * Single-source-of-truth composite instantiation. Used by every load path:
 *
 *   - `SceneManifestLoader` (manifest-level globals / out-of-bounds composites)
 *   - `TiledObjectStreamer` (per-tile streaming)
 *   - asset-manager `PlacedCompositesRegistry` (live editor preview)
 *
 * The merge order (template default ← placement state ← runtime override) and
 * the `Scene.compositeInstances` registration MUST stay in lockstep across
 * paths or runtime state will silently desync; centralising them here removes
 * the "keep these four sites in sync" maintenance burden.
 *
 * Pre-condition: `def.slots.mesh.modelId` is already in `scene.modelRegistry`.
 * The editor preview loads on demand; the runtime path pre-warms via
 * `manifest.assets.models`.
 */
export function instantiateComposite(
  scene: Scene,
  def: CompositeDef,
  placement: SceneCompositeInstance,
): CompositeInstance {
  const template = requireTemplate(def.template);
  const override = scene.compositeStateOverrides.get(placement.instanceId);
  const merged = resolveState(template, placement, override);
  const instance = template.buildRuntime(def, placement, merged, scene);
  scene.compositeInstances.set(placement.instanceId, instance);
  return instance;
}
